/**
 * MediaLightbox — full-screen viewer for images / videos with prev/next navigation, zoom, and swipe-to-close.
 * Usage:
 *   const [lb, setLb] = useState(null);
 *   ...
 *   <img onClick={() => setLb({ items: [{ url, type: 'image' }], index: 0 })} />
 *   {lb && <MediaLightbox items={lb.items} startIndex={lb.index} onClose={() => setLb(null)} />}
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';

function detectType(url) {
  if (!url) return 'image';
  return /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i.test(url) ? 'video' : 'image';
}

export default function MediaLightbox({ items = [], startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const touchStart = useRef(null);

  const total = items.length;
  const current = items[index] || items[0];
  const type = current?.type || detectType(current?.url);

  const next = useCallback(() => { setZoom(1); setDrag({ x: 0, y: 0 }); setIndex((i) => (i + 1) % total); }, [total]);
  const prev = useCallback(() => { setZoom(1); setDrag({ x: 0, y: 0 }); setIndex((i) => (i - 1 + total) % total); }, [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(4, z + 0.25));
      else if (e.key === '-') setZoom((z) => Math.max(1, z - 0.25));
      else if (e.key === '0') { setZoom(1); setDrag({ x: 0, y: 0 }); }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [next, prev, onClose]);

  const onTouchStart = (e) => { touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    if (Math.abs(dy) > 90 && Math.abs(dy) > Math.abs(dx)) { onClose?.(); return; }
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) { dx < 0 ? next() : prev(); }
    touchStart.current = null;
  };

  const download = () => {
    if (!current?.url) return;
    const a = document.createElement('a');
    a.href = current.url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = '';
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none"
         data-testid="media-lightbox"
         onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3 text-white bg-gradient-to-b from-black/80 to-transparent">
        <div className="text-xs font-black opacity-90" data-testid="media-lightbox-counter">
          {index + 1} / {total}
        </div>
        <div className="flex items-center gap-1.5">
          {type === 'image' && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.max(1, z - 0.25)); }}
                      data-testid="media-lightbox-zoom-out"
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center font-black">−</button>
              <span className="text-[10px] opacity-70 w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.min(4, z + 0.25)); }}
                      data-testid="media-lightbox-zoom-in"
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center font-black">+</button>
              <button onClick={(e) => { e.stopPropagation(); setZoom(1); setDrag({ x: 0, y: 0 }); }}
                      data-testid="media-lightbox-reset"
                      className="px-2 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center text-[10px] font-black">Reset</button>
            </>
          )}
          <button onClick={(e) => { e.stopPropagation(); download(); }}
                  data-testid="media-lightbox-download"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">⬇</button>
          <button onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                  data-testid="media-lightbox-close"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-lg">✕</button>
        </div>
      </div>

      {/* Prev/Next arrows (only when multiple) */}
      {total > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }}
                  data-testid="media-lightbox-prev"
                  className="absolute left-2 sm:left-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl font-black flex items-center justify-center">‹</button>
          <button onClick={(e) => { e.stopPropagation(); next(); }}
                  data-testid="media-lightbox-next"
                  className="absolute right-2 sm:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl font-black flex items-center justify-center">›</button>
        </>
      )}

      {/* Media */}
      <div className="w-full h-full flex items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
        {type === 'video' ? (
          <video src={current.url} controls autoPlay
                 className="max-h-[85vh] max-w-[95vw] rounded-lg shadow-2xl"
                 data-testid="media-lightbox-video" />
        ) : (
          <img src={current.url} alt=""
               style={{ transform: `translate(${drag.x}px, ${drag.y}px) scale(${zoom})`, transition: 'transform 0.15s ease-out', cursor: zoom > 1 ? 'grab' : 'zoom-in' }}
               onClick={() => { if (zoom === 1) setZoom(2); else { setZoom(1); setDrag({ x: 0, y: 0 }); } }}
               onMouseDown={(e) => {
                 if (zoom <= 1) return;
                 const sx = e.clientX - drag.x, sy = e.clientY - drag.y;
                 const move = (ev) => setDrag({ x: ev.clientX - sx, y: ev.clientY - sy });
                 const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
                 document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
               }}
               className="max-h-[85vh] max-w-[95vw] rounded-lg shadow-2xl object-contain"
               data-testid="media-lightbox-image" />
        )}
      </div>

      {/* Caption */}
      {current.caption && (
        <div className="absolute bottom-4 inset-x-0 text-center text-xs text-white/90 px-4">
          <span className="inline-block bg-black/60 rounded-full px-3 py-1" data-testid="media-lightbox-caption">{current.caption}</span>
        </div>
      )}
    </div>
  );
}

// Small helper to open lightbox from a click handler in parent
export function normalizeItems(urls = []) {
  return urls.filter(Boolean).map((u) => ({ url: u, type: detectType(u) }));
}
